import { useState, FormEvent, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { apiClient } from '../api/client';
import { useTranslation } from '../i18n';
import { DirectionalIcon } from '../components/ui/DirectionalIcon';
import analytics from '../lib/analytics';

const PASSWORD_RULES = {
  minLength: 8,
  maxLength: 12,
  hasLetter: /[a-zA-Z]/,
  hasNumber: /[0-9]/,
  hasSpecial: /[^a-zA-Z0-9]/,
};

type Step = 'forgot' | 'reset';

export default function ResetPasswordPage() {
  const [searchParams] = useSearchParams();
  const [step, setStep] = useState<Step>('forgot');

  // Forgot password step state
  const [forgotIdentifier, setForgotIdentifier] = useState('');
  const [forgotLoading, setForgotLoading] = useState(false);
  const [forgotSuccess, setForgotSuccess] = useState(false);

  // Reset password step state
  const [resetEmail, setResetEmail] = useState('');
  const [resetToken, setResetToken] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [resetLoading, setResetLoading] = useState(false);

  const [error, setError] = useState<string | null>(null);

  const navigate = useNavigate();
  const { t, direction } = useTranslation();

  useEffect(() => {
    // Check if URL has token and email - if so, skip to reset step
    const email = searchParams.get('email');
    const token = searchParams.get('token');
    if (email && token) {
      setResetEmail(email);
      setResetToken(token);
      setStep('reset');
    }
  }, [searchParams]);

  const validatePassword = (pwd: string) => ({
    length: pwd.length >= PASSWORD_RULES.minLength && pwd.length <= PASSWORD_RULES.maxLength,
    hasLetter: PASSWORD_RULES.hasLetter.test(pwd),
    hasNumber: PASSWORD_RULES.hasNumber.test(pwd),
    hasSpecial: PASSWORD_RULES.hasSpecial.test(pwd),
  });

  const passwordValidation = validatePassword(password);
  const isPasswordValid = Object.values(passwordValidation).every(v => v);
  const passwordsMatch = password === confirmPassword && password.length > 0;

  async function handleForgotSubmit(e: FormEvent) {
    e.preventDefault();
    setError(null);

    if (!forgotIdentifier) {
      setError(t('all_fields_required') || 'Please enter your email address');
      return;
    }

    setForgotLoading(true);
    analytics.trackPasswordResetRequested();

    try {
      await apiClient.post('/auth/forgot-password', { email_or_phone: forgotIdentifier });
      setForgotSuccess(true);
    } catch (err) {
      if (axios.isAxiosError(err)) {
        if (err.response?.status === 429) {
          setError(t('rate_limit_exceeded') || 'Too many requests. Please try again later.');
          analytics.trackPasswordResetFailed('rate_limit');
        } else {
          setError(t('something_wrong') || 'Something went wrong. Please try again.');
          analytics.trackPasswordResetFailed('unknown');
        }
      } else {
        setError(t('something_wrong') || 'Something went wrong');
        analytics.trackPasswordResetFailed('network_error');
      }
    } finally {
      setForgotLoading(false);
    }
  }

  async function handleResetSubmit(e: FormEvent) {
    e.preventDefault();
    setError(null);

    if (!resetEmail || !resetToken || !password || !confirmPassword) {
      setError(t('all_fields_required') || 'Please fill in all fields');
      return;
    }

    if (!isPasswordValid) {
      setError(t('password_requirements') || 'Password does not meet requirements');
      return;
    }

    if (!passwordsMatch) {
      setError(t('passwords_not_matching') || 'Passwords do not match');
      return;
    }

    setResetLoading(true);

    try {
      await apiClient.post('/auth/reset-password', { email: resetEmail, token: resetToken, password });
      analytics.trackPasswordResetCompleted();
      navigate('/login', { replace: true });
    } catch (err) {
      if (axios.isAxiosError(err)) {
        const errorCode = err.response?.data?.error?.code;
        if (errorCode === 'RESET_TOKEN_INVALID') {
          setError(t('invalid_token') || 'Invalid or expired token. Please request a new reset link.');
          analytics.trackPasswordResetFailed('invalid_token');
          setStep('forgot');
          setResetToken('');
          setResetEmail('');
        } else if (errorCode === 'RESET_TOKEN_EXPIRED') {
          setError(t('token_expired') || 'Reset link has expired. Please request a new one.');
          analytics.trackPasswordResetFailed('expired_token');
          setStep('forgot');
          setResetToken('');
          setResetEmail('');
        } else if (err.response?.status === 422) {
          setError(t('invalid_input') || 'Invalid input. Please check your information.');
          analytics.trackPasswordResetFailed('invalid_input');
        } else {
          setError(t('password_reset_failed') || 'Failed to reset password');
          analytics.trackPasswordResetFailed('unknown');
        }
      } else {
        setError(t('something_wrong') || 'Something went wrong');
        analytics.trackPasswordResetFailed('network_error');
      }
    } finally {
      setResetLoading(false);
    }
  }

  const isForgotStep = step === 'forgot';

  return (
    <div dir={direction} style={{
      // See LoginPage: a fixed 884px floor made the Android keyboard scroll the
      // whole page on input focus. dvh tracks the keyboard-aware viewport.
      minHeight: '100dvh',
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      justifyContent: 'center',
      background: 'var(--surface)',
      padding: '1.5rem',
    }}>
      {/* Background blur blobs */}
      <div style={{
        position: 'fixed', zIndex: -1,
        top: '-10%', right: '-10%',
        width: '500px', height: '500px',
        background: 'rgba(0,91,192,0.05)',
        borderRadius: '50%', filter: 'blur(120px)',
        pointerEvents: 'none',
      }} />
      <div style={{
        position: 'fixed', zIndex: -1,
        bottom: '-10%', left: '-10%',
        width: '500px', height: '500px',
        background: 'rgba(95,92,120,0.05)',
        borderRadius: '50%', filter: 'blur(120px)',
        pointerEvents: 'none',
      }} />

      <main style={{ width: '100%', maxWidth: '420px' }}>
        {/* Logo */}
        <div style={{ marginBottom: '3rem', textAlign: 'center' }}>
          <h1 style={{
            fontFamily: 'var(--manrope)',
            fontSize: '2.25rem',
            fontWeight: 800,
            letterSpacing: '-0.04em',
            color: 'var(--primary)',
            margin: 0,
          }}>Lot'N'Go</h1>
          <p className="body-md" style={{ marginTop: '0.5rem', letterSpacing: '0.03em' }}>
            {isForgotStep
              ? (t('forgot_password_title') || 'Forgot Your Password?')
              : (t('reset_password_title') || 'Reset Your Password')}
          </p>
        </div>

        {/* Card */}
        <div style={{
          background: 'var(--surface-lowest)',
          borderRadius: '12px',
          padding: '2.5rem',
          boxShadow: 'var(--whisper)',
          display: 'flex',
          flexDirection: 'column',
          gap: '2rem',
        }}>
          {/* Forgot Password Step */}
          {isForgotStep && (
            <form onSubmit={handleForgotSubmit} style={{ display: 'flex', flexDirection: 'column', gap: '1.5rem' }}>
              {forgotSuccess ? (
                <>
                  <div style={{
                    background: 'rgba(34,197,94,0.1)',
                    border: '1px solid rgba(34,197,94,0.3)',
                    borderRadius: '8px',
                    padding: '1rem',
                    textAlign: 'center',
                  }}>
                    <p style={{ color: 'var(--on-surface)', margin: '0 0 0.5rem 0' }}>
                      {t('check_email') || '✓ Check your email'}
                    </p>
                    <p style={{ color: 'var(--on-surface-2)', fontSize: '0.875rem', margin: 0 }}>
                      {t('reset_link_sent') || "We've sent a password reset link to your email address. Click the link to continue."}
                    </p>
                  </div>
                  <button
                    type="button"
                    onClick={() => {
                      setForgotIdentifier('');
                      setForgotSuccess(false);
                      setError(null);
                    }}
                    style={{
                      width: '100%',
                      padding: '1rem',
                      borderRadius: '8px',
                      border: 'none',
                      background: 'linear-gradient(135deg, #005bc0 0%, #004fa9 100%)',
                      color: 'var(--on-primary)',
                      fontFamily: 'var(--manrope)',
                      fontSize: '15px',
                      fontWeight: 700,
                      cursor: 'pointer',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      gap: '0.5rem',
                      boxShadow: '0 4px 14px rgba(0,91,192,0.25)',
                    }}
                  >
                    {t('send_again') || 'Send Another Link'}
                  </button>
                </>
              ) : (
                <>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
                    <label className="form-label" htmlFor="forgot-email">{t('email_or_phone')}</label>
                    <div style={{ position: 'relative' }}>
                      <span className="material-symbols-outlined" style={{
                        position: 'absolute',
                        [direction === 'rtl' ? 'right' : 'left']: '1rem',
                        top: '50%',
                        transform: 'translateY(-50%)',
                        color: 'var(--on-surface-3)', fontSize: '20px',
                        pointerEvents: 'none',
                      }}>mail</span>
                      <input
                        id="forgot-email"
                        type="text"
                        value={forgotIdentifier}
                        onChange={(e) => setForgotIdentifier(e.target.value)}
                        placeholder="e.g., name@email.com or +123456789"
                        required
                        autoComplete="email"
                        style={{
                          width: '100%', boxSizing: 'border-box',
                          paddingLeft: direction === 'rtl' ? '1rem' : '3rem',
                          paddingRight: direction === 'rtl' ? '3rem' : '1rem',
                          paddingTop: '1rem', paddingBottom: '1rem',
                          borderRadius: '8px', border: 'none',
                          background: 'var(--surface-low)',
                          fontFamily: 'var(--inter)', fontSize: '14px',
                          color: 'var(--on-surface)',
                          outline: '2px solid transparent',
                          transition: 'outline 0.15s',
                          textAlign: direction === 'rtl' ? 'right' : 'left',
                        }}
                      />
                    </div>
                  </div>

                  {error && (
                    <p style={{ color: 'var(--error)', fontSize: '0.875rem', margin: 0 }}>{error}</p>
                  )}

                  <button
                    type="submit"
                    disabled={!forgotIdentifier || forgotLoading}
                    style={{
                      width: '100%',
                      padding: '1rem',
                      borderRadius: '8px',
                      border: 'none',
                      background: 'linear-gradient(135deg, #005bc0 0%, #004fa9 100%)',
                      color: 'var(--on-primary)',
                      fontFamily: 'var(--manrope)',
                      fontSize: '15px',
                      fontWeight: 700,
                      cursor: (!forgotIdentifier || forgotLoading) ? 'not-allowed' : 'pointer',
                      opacity: (!forgotIdentifier || forgotLoading) ? 0.7 : 1,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      gap: '0.5rem',
                      boxShadow: '0 4px 14px rgba(0,91,192,0.25)',
                    }}
                  >
                    {forgotLoading ? t('sending') || 'Sending...' : (t('send_reset_link') || 'Send Reset Link')}
                    {!forgotLoading && (
                      <DirectionalIcon icon="arrow_forward" style={{ fontSize: '20px' }} />
                    )}
                  </button>
                </>
              )}
            </form>
          )}

          {/* Reset Password Step */}
          {!isForgotStep && (
            <form onSubmit={handleResetSubmit} style={{ display: 'flex', flexDirection: 'column', gap: '1.5rem' }}>
              {/* Email (read-only, pre-filled) */}
              <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
                <label className="form-label" htmlFor="reset-email">{t('email_address')}</label>
                <div style={{ position: 'relative' }}>
                  <span className="material-symbols-outlined" style={{
                    position: 'absolute',
                    [direction === 'rtl' ? 'right' : 'left']: '1rem',
                    top: '50%',
                    transform: 'translateY(-50%)',
                    color: 'var(--on-surface-3)', fontSize: '20px',
                    pointerEvents: 'none',
                  }}>mail</span>
                  <input
                    id="reset-email"
                    type="email"
                    value={resetEmail}
                    disabled
                    style={{
                      width: '100%', boxSizing: 'border-box',
                      paddingLeft: direction === 'rtl' ? '1rem' : '3rem',
                      paddingRight: direction === 'rtl' ? '3rem' : '1rem',
                      paddingTop: '1rem', paddingBottom: '1rem',
                      borderRadius: '8px', border: 'none',
                      background: 'var(--surface-high)',
                      fontFamily: 'var(--inter)', fontSize: '14px',
                      color: 'var(--on-surface-2)',
                      opacity: 0.6,
                      textAlign: direction === 'rtl' ? 'right' : 'left',
                    }}
                  />
                </div>
              </div>

              {/* New Password */}
              <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
                <label className="form-label" htmlFor="reset-password">{t('new_password') || 'New Password'}</label>
                <div style={{ position: 'relative' }}>
                  <span className="material-symbols-outlined" style={{
                    position: 'absolute',
                    [direction === 'rtl' ? 'right' : 'left']: '1rem',
                    top: '50%',
                    transform: 'translateY(-50%)',
                    color: 'var(--on-surface-3)', fontSize: '20px',
                    pointerEvents: 'none',
                  }}>lock</span>
                  <input
                    id="reset-password"
                    type={showPassword ? 'text' : 'password'}
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    placeholder="••••••••"
                    required
                    autoComplete="new-password"
                    style={{
                      width: '100%', boxSizing: 'border-box',
                      paddingLeft: direction === 'rtl' ? '1rem' : '3rem',
                      paddingRight: direction === 'rtl' ? '3rem' : '3rem',
                      paddingTop: '1rem', paddingBottom: '1rem',
                      borderRadius: '8px', border: 'none',
                      background: 'var(--surface-low)',
                      fontFamily: 'var(--inter)', fontSize: '14px',
                      color: 'var(--on-surface)',
                      outline: '2px solid transparent',
                      transition: 'outline 0.15s',
                      textAlign: direction === 'rtl' ? 'right' : 'left',
                    }}
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    style={{
                      position: 'absolute',
                      [direction === 'rtl' ? 'left' : 'right']: '1rem',
                      top: '50%',
                      transform: 'translateY(-50%)',
                      background: 'none',
                      border: 'none',
                      cursor: 'pointer',
                      padding: 0,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                    }}
                  >
                    <span className="material-symbols-outlined" style={{
                      fontSize: '20px',
                      color: 'var(--on-surface-3)',
                    }}>
                      {showPassword ? 'visibility' : 'visibility_off'}
                    </span>
                  </button>
                </div>
              </div>

              {/* Password Requirements */}
              {password && (
                <div style={{
                  background: 'var(--surface-high)',
                  borderRadius: '8px',
                  padding: '1rem',
                  display: 'flex',
                  flexDirection: 'column',
                  gap: '0.5rem',
                }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', fontSize: '12px' }}>
                    <span style={{ color: passwordValidation.length ? 'var(--primary)' : 'var(--on-surface-3)' }}>
                      {passwordValidation.length ? '✓' : '○'}
                    </span>
                    <span>{PASSWORD_RULES.minLength}-{PASSWORD_RULES.maxLength} characters</span>
                  </div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', fontSize: '12px' }}>
                    <span style={{ color: passwordValidation.hasLetter ? 'var(--primary)' : 'var(--on-surface-3)' }}>
                      {passwordValidation.hasLetter ? '✓' : '○'}
                    </span>
                    <span>{t('password_letter') || 'At least one letter'}</span>
                  </div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', fontSize: '12px' }}>
                    <span style={{ color: passwordValidation.hasNumber ? 'var(--primary)' : 'var(--on-surface-3)' }}>
                      {passwordValidation.hasNumber ? '✓' : '○'}
                    </span>
                    <span>{t('password_number') || 'At least one number'}</span>
                  </div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', fontSize: '12px' }}>
                    <span style={{ color: passwordValidation.hasSpecial ? 'var(--primary)' : 'var(--on-surface-3)' }}>
                      {passwordValidation.hasSpecial ? '✓' : '○'}
                    </span>
                    <span>{t('password_special') || 'At least one special character'}</span>
                  </div>
                </div>
              )}

              {/* Confirm Password */}
              <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
                <label className="form-label" htmlFor="confirm-reset-password">{t('confirm_password') || 'Confirm Password'}</label>
                <div style={{ position: 'relative' }}>
                  <span className="material-symbols-outlined" style={{
                    position: 'absolute',
                    [direction === 'rtl' ? 'right' : 'left']: '1rem',
                    top: '50%',
                    transform: 'translateY(-50%)',
                    color: 'var(--on-surface-3)', fontSize: '20px',
                    pointerEvents: 'none',
                  }}>lock</span>
                  <input
                    id="confirm-reset-password"
                    type={showConfirmPassword ? 'text' : 'password'}
                    value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e.target.value)}
                    placeholder="••••••••"
                    required
                    autoComplete="new-password"
                    style={{
                      width: '100%', boxSizing: 'border-box',
                      paddingLeft: direction === 'rtl' ? '1rem' : '3rem',
                      paddingRight: direction === 'rtl' ? '3rem' : '3rem',
                      paddingTop: '1rem', paddingBottom: '1rem',
                      borderRadius: '8px', border: 'none',
                      background: 'var(--surface-low)',
                      fontFamily: 'var(--inter)', fontSize: '14px',
                      color: 'var(--on-surface)',
                      outline: '2px solid transparent',
                      transition: 'outline 0.15s',
                      textAlign: direction === 'rtl' ? 'right' : 'left',
                    }}
                  />
                  <button
                    type="button"
                    onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                    style={{
                      position: 'absolute',
                      [direction === 'rtl' ? 'left' : 'right']: '1rem',
                      top: '50%',
                      transform: 'translateY(-50%)',
                      background: 'none',
                      border: 'none',
                      cursor: 'pointer',
                      padding: 0,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                    }}
                  >
                    <span className="material-symbols-outlined" style={{
                      fontSize: '20px',
                      color: 'var(--on-surface-3)',
                    }}>
                      {showConfirmPassword ? 'visibility' : 'visibility_off'}
                    </span>
                  </button>
                </div>
                {confirmPassword && !passwordsMatch && (
                  <p style={{ color: 'var(--error)', fontSize: '0.875rem', margin: '0.25rem 0 0 0' }}>
                    {t('passwords_not_matching') || 'Passwords do not match'}
                  </p>
                )}
              </div>

              {error && (
                <p style={{ color: 'var(--error)', fontSize: '0.875rem', margin: 0 }}>{error}</p>
              )}

              {/* Submit */}
              <button
                type="submit"
                disabled={resetLoading || !isPasswordValid || !passwordsMatch || !resetToken}
                style={{
                  width: '100%',
                  padding: '1rem',
                  borderRadius: '8px',
                  border: 'none',
                  background: 'linear-gradient(135deg, #005bc0 0%, #004fa9 100%)',
                  color: 'var(--on-primary)',
                  fontFamily: 'var(--manrope)',
                  fontSize: '15px',
                  fontWeight: 700,
                  cursor: (resetLoading || !isPasswordValid || !passwordsMatch || !resetToken) ? 'not-allowed' : 'pointer',
                  opacity: (resetLoading || !isPasswordValid || !passwordsMatch || !resetToken) ? 0.7 : 1,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: '0.5rem',
                  boxShadow: '0 4px 14px rgba(0,91,192,0.25)',
                }}
              >
                {resetLoading ? t('resetting') || 'Resetting...' : t('reset_password') || 'Reset Password'}
                {!resetLoading && (
                  <DirectionalIcon icon="arrow_forward" style={{ fontSize: '20px' }} />
                )}
              </button>
            </form>
          )}

          {/* Back to login link */}
          <div style={{ textAlign: 'center' }}>
            <button
              onClick={() => navigate('/login', { replace: true })}
              style={{
                background: 'none',
                border: 'none',
                color: 'var(--primary)',
                fontFamily: 'var(--manrope)',
                fontSize: '14px',
                fontWeight: 600,
                cursor: 'pointer',
                textDecoration: 'none',
                padding: 0,
              }}
            >
              {t('back_to_login') || 'Back to Login'}
            </button>
          </div>
        </div>
      </main>

      {/* Bottom gradient strip */}
      <div style={{
        position: 'fixed', bottom: 0, left: 0,
        width: '100%', height: '4px',
        background: 'linear-gradient(to right, #005bc0, #d8e2ff, #005bc0)',
        pointerEvents: 'none',
      }} />
    </div>
  );
}
